import React, { FC, useEffect, useRef, useState } from "react";

import styled from "styled-components";

import { ScrollChevron, ScrollHint, useScrollAffordance } from "@/components/CartModal/phoneTemplate";
import { logout, restoreAccountFromDeletion } from "@/store/actions/user";
import { useAppDispatch, useAppDispatchUnwrap } from "@/store/hooks";
import { colors, fonts, fontSizes, radius } from "@/styles/design-system";
import { ERoutes } from "@/utils/constants";
import { setSession } from "@/utils/jwt";

/* Blocking modal shown on login when the backend returns
   ACCOUNT_SCHEDULED_FOR_DELETION. The X dismisses by signing the user
   out (the limited grace tokens shouldn't survive an abandoned recovery
   attempt). Visual treatment mirrors the /account-deleted page. */

interface IDialog {
  open: boolean;
  permanentDeleteOn: string | null;
  /** Email the user just attempted to log in with — surfaced below the
      heading so they're sure which account is being recovered. */
  userEmail: string | null;
  /** Whether the account still has an active subscription on hold
      (cancel_at scheduled). Drives the conditional recovery copy. */
  hadSubscription: boolean;
  /** Formatted long-date when the subscription actually ends —
      `min(cycle_end, day-30)`. Same value the email template uses. */
  subEndsOn: string | null;
  /** Backend handed us session tokens with the grace-state response so
      the restore POST can authenticate. Passed in by the LoginDialog. */
  sessionTokens: {
    accessToken: string;
    refreshToken: string;
    accessTokenExpiresAt?: string;
    refreshTokenExpiresAt?: string;
  } | null;
  onClose: () => void;
}

const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1400;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: ${({ $open }) => ($open ? "auto" : "none")};
`;

const ModalContainer = styled.div<{ $open?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  background:
    radial-gradient(ellipse 45% 40% at 42% -3%, rgba(176, 144, 105, 0.9) 0%, rgba(160, 128, 90, 0.5) 35%, transparent 75%),
    radial-gradient(ellipse 55% 50% at 58% 3%, rgba(170, 140, 100, 0.75) 0%, rgba(140, 110, 78, 0.35) 45%, transparent 85%),
    linear-gradient(180deg, rgb(153, 112, 75) 0%, rgb(99, 74, 54) 50%, rgb(111, 83, 60) 100%);
  border-radius: 24px;
  box-shadow: ${({ $open }) => ($open ? "0 0 0 9999px rgba(0, 0, 0, 0.78), 0 20px 60px rgba(0, 0, 0, 0.6)" : "none")};
  width: 100%;
  max-width: 560px;
  max-height: 95vh;
  overflow: hidden;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transform: translateY(${({ $open }) => ($open ? "0" : "8px")});
  transition: opacity 0.25s ease, transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  margin: 0 16px;

  @media (max-width: 600px) {
    margin: 0 14px;
  }
`;

/* Fixed header (no ✕ — this is a deliberately blocking modal). */
const Header = styled.div`
  flex-shrink: 0;
  padding: 44px 40px 0;
  @media (max-width: 600px) {
    padding: 28px 22px 0;
  }
`;

/* Scrollable body — only this scrolls; the action buttons stay pinned below. */
const ScrollBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 20px 40px 0;
  text-align: left;
  color: #f7ede6;
  @media (max-width: 600px) {
    padding: 16px 22px 0;
  }
`;

/* Fixed footer — the Recover / Logout buttons are ALWAYS visible (no ✕, so the
   action is the only way forward; it must never scroll out of reach). */
const Footer = styled.div`
  flex-shrink: 0;
  position: relative;
  padding: 14px 40px 36px;
  @media (max-width: 600px) {
    padding: 12px 22px 24px;
  }
`;

/* Scroll affordance — appears only if the body overflows above the footer
   (phone/tablet; desktop has the native scrollbar). Anchored to the footer's
   top edge so it overlays the body's last 52px. */
const ScrollFade = styled.div<{ $visible: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 100%;
  height: 52px;
  pointer-events: none;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 6px;
  background: linear-gradient(to bottom, rgba(111, 83, 60, 0) 0%, rgba(111, 83, 60, 0.8) 52%, rgba(111, 83, 60, 1) 100%);
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.25s ease;
  @media (min-width: 1150px) {
    display: none;
  }
`;

const TitleRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 16px;
  margin-bottom: 20px;
`;

const IconCircle = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  border: 2px solid rgba(235, 194, 142, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 32px;
    height: 32px;
  }
`;

const Title = styled.h2`
  font-family: ${fonts.heading};
  font-size: 1.75rem;
  font-weight: 500;
  line-height: 1.15;
  margin: 0;
  color: #fff;
  text-align: center;

  @media (max-width: 600px) {
    font-size: 1.5rem;
  }
`;

const EmailLine = styled.div`
  font-family: ${fonts.body};
  font-size: 0.9rem;
  color: #f7ede6;
  opacity: 0.85;
  margin-top: -8px;
  word-break: break-all;
  text-align: center;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(235, 194, 142, 0.2);
  margin: 4px 0 0;
`;

const Body = styled.p`
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  line-height: 1.6;
  margin: 0 0 16px;
  color: #f7ede6;

  a { color: #ffdca8; text-decoration: underline; }
  strong { color: #fff; font-weight: 600; }
`;

const RecoverBtn = styled.button<{ $disabled?: boolean }>`
  width: 100%;
  padding: 14px 28px;
  border-radius: ${radius.md};
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: 600;
  letter-spacing: 0.03em;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  border: none;
  background: ${({ $disabled }) => ($disabled ? "rgba(156, 211, 253, 0.4)" : colors.accent.blue)};
  color: #1a3a52;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  box-shadow: ${({ $disabled }) => ($disabled ? "none" : "0 0 28px rgba(156, 211, 253, 0.35)")};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};

  &:hover {
    transform: ${({ $disabled }) => ($disabled ? "none" : "translateY(-2px)")};
    box-shadow: ${({ $disabled }) => ($disabled ? "none" : "0 0 38px rgba(156, 211, 253, 0.55)")};
    background: ${({ $disabled }) => ($disabled ? "rgba(156, 211, 253, 0.4)" : "#b6dffd")};
  }
`;

/* Logout — a plain blue text link below the Recover button (not a button).
   display:block + fit-content width so the auto margins centre the compact
   link (a default inline-block button ignores `margin: auto`). */
const LogoutBtn = styled.button<{ $disabled?: boolean }>`
  display: block;
  width: fit-content;
  margin: 14px auto 0;
  padding: 4px 8px;
  background: none;
  border: none;
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  font-weight: 500;
  color: ${colors.accent.blue};
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};

  @media (hover: hover) {
    &:hover { text-decoration: ${({ $disabled }) => ($disabled ? "none" : "underline")}; }
  }
`;

/* Hourglass — matches the /account-deleted page icon so the two
   surfaces (success page + login-time restore modal) stay visually
   consistent. Gold stroke matches the IconCircle border. */
const HourglassIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#ebc28e" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2h12" />
    <path d="M6 22h12" />
    <path d="M6 2v4a6 6 0 0 0 12 0V2" />
    <path d="M6 22v-4a6 6 0 0 1 12 0v4" />
  </svg>
);

const AccountRestoreModal: FC<IDialog> = ({ open, permanentDeleteOn, userEmail, hadSubscription, subEndsOn, sessionTokens, onClose }) => {
  const dispatch = useAppDispatch();
  const dispatchUnwrap = useAppDispatchUnwrap();
  const [restoring, setRestoring] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const canScrollDown = useScrollAffordance(bodyRef);

  // Stash the limited-session tokens so the upcoming /restore-from-deletion
  // POST authenticates. The dispatch+page reload after restore picks up
  // the user properly via the normal /users/my call.
  useEffect(() => {
    if (open && sessionTokens) {
      setSession({
        accessToken: sessionTokens.accessToken,
        refreshToken: sessionTokens.refreshToken,
        accessTokenSecondToExpiration: sessionTokens.accessTokenExpiresAt as any,
        refreshTokenSecondToExpiration: sessionTokens.refreshTokenExpiresAt as any,
      });
    }
  }, [open, sessionTokens]);

  const handleRestore = async () => {
    if (restoring) return;
    setRestoring(true);
    try {
      await dispatchUnwrap(restoreAccountFromDeletion());
      // Flag the just-completed recovery so the AccountRecoveredGlobalBanner
      // surfaces a persistent green confirmation across the upcoming page
      // reload. The dismiss key (per-timestamp scope) lets a future
      // recovery on the same browser re-show. localStorage wipes on
      // logout per the standard convention, so the flag doesn't leak to
      // a different user logging in after.
      try {
        localStorage.setItem("account-just-recovered-at", new Date().toISOString());
      } catch { /* non-fatal */ }
      // Hard-navigate home so the app fully reinitializes with the
      // restored user (clears any stale auth state in memory).
      window.location.href = ERoutes.Home;
    } catch {
      setRestoring(false);
      // If restoration fails (e.g. grace expired during the modal),
      // best path is to log out and let the user see the standard
      // login flow's rejection.
      try {
        const tokens = JSON.parse(localStorage.getItem("tokens") || "{}");
        await dispatch(logout({ accessToken: tokens?.accessToken || "", refreshToken: tokens?.refreshToken || "" }));
      } catch { /* non-fatal */ }
      window.location.href = ERoutes.Home;
    }
  };

  const handleClose = async () => {
    if (restoring) return;
    // Sign the user out — abandoning recovery means the limited-session
    // tokens shouldn't survive. The account stays scheduled for deletion.
    try {
      const tokens = JSON.parse(localStorage.getItem("tokens") || "{}");
      await dispatch(logout({ accessToken: tokens?.accessToken || "", refreshToken: tokens?.refreshToken || "" }));
    } catch { /* non-fatal */ }
    onClose();
  };

  return (
    <Overlay $open={open}>
      <ModalContainer $open={open} onClick={(e) => e.stopPropagation()}>
        <Header>
          <TitleRow>
            <IconCircle><HourglassIcon /></IconCircle>
            <Title>Account Deletion Scheduled</Title>
            {userEmail ? <EmailLine>{userEmail}</EmailLine> : null}
          </TitleRow>
          <Divider />
        </Header>

        <ScrollBody ref={bodyRef}>
          <Body>
            Your Audio Sanctum account is scheduled for permanent deletion
            {permanentDeleteOn ? <> on <strong>{permanentDeleteOn}</strong></> : null}.
            {" "}After that date, your personal data will be permanently removed.
            Past invoices, audit logs and resolved claims are retained
            (see our <a href={ERoutes.Privacy}>privacy policy</a> for details).
          </Body>
          <Body>
            Recover your account now to keep all your data and continue using
            Audio Sanctum
            {hadSubscription ? (
              <>{" "}— your subscription will continue normally if you recover
                {subEndsOn ? <> before <strong>{subEndsOn}</strong></> : (permanentDeleteOn ? <> before <strong>{permanentDeleteOn}</strong></> : null)}</>
            ) : null}
            . Otherwise, log out below to abandon recovery.
          </Body>
        </ScrollBody>

        <Footer>
          <ScrollFade $visible={canScrollDown} aria-hidden="true"><ScrollHint><span>Scroll</span><ScrollChevron /></ScrollHint></ScrollFade>
          <RecoverBtn $disabled={restoring} onClick={handleRestore}>
            {restoring ? "Recovering…" : "Recover Account Now"}
          </RecoverBtn>
          <LogoutBtn $disabled={restoring} onClick={handleClose}>
            Logout
          </LogoutBtn>
        </Footer>
      </ModalContainer>
    </Overlay>
  );
};

export default AccountRestoreModal;
